import React, { useEffect, useState } from 'react';
import { Modal, Button, message, Space, Typography } from 'antd';
import { QrcodeOutlined, CloseOutlined } from '@ant-design/icons';
import WechatBindingModal from '@/components/WechatBindingModal';
import { checkWechatBindingStatus } from '@/services/ant-design-pro/wechatBinding';

const { Title, Text } = Typography;

interface WechatBindingCheckerProps {
  onBindingSuccess?: () => void;
  triggerReminder?: boolean; // 外部触发显示提醒框
  onReminderTriggered?: () => void; // 提醒框显示后的回调
}

const WechatBindingChecker: React.FC<WechatBindingCheckerProps> = ({
  onBindingSuccess,
  triggerReminder = false,
  onReminderTriggered,
}) => {
  const [showBindingModal, setShowBindingModal] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [bindingStatus, setBindingStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // 检查绑定状态
  const checkBindingStatus = async () => {
    try {
      setLoading(true);
      const response = await checkWechatBindingStatus();
      // 响应拦截器已经处理了数据格式，response.data 就是 { is_bound, wechat_info }
      // 兼容处理：支持 response.data 或直接 response
      const data = response?.data || response;
      setBindingStatus(data);

      // 如果已绑定，直接返回，不弹出提醒
      if (data?.is_bound === true || data?.is_bound === 'true') {
        return;
      }

      // 如果未绑定，检查是否已经在本会话中显示过提醒
      const hasShownWechatReminderThisSession = sessionStorage.getItem('wechatBindingReminderShownThisSession');

      // 如果还没有显示过提醒，则显示
      if (hasShownWechatReminderThisSession !== 'true') {
        setShowReminder(true);
      }
    } catch (error) {
      // 检查绑定状态失败，静默处理
    } finally {
      setLoading(false);
    }
  };

  // 处理外部触发提醒框
  useEffect(() => {
    if (triggerReminder) {
      // 清除提醒标记，强制显示提醒框
      sessionStorage.removeItem('wechatBindingReminderShownThisSession');
      setShowReminder(true);
      onReminderTriggered?.();
    }
  }, [triggerReminder, onReminderTriggered]);

  // 组件挂载时检查绑定状态
  useEffect(() => {
    // 如果外部触发，不执行自动检查
    if (triggerReminder) {
      return;
    }

    // 检查是否是从登录页面跳转过来的
    const urlParams = new URLSearchParams(window.location.search);
    const isFromLogin = urlParams.get('from') === 'login' ||
                       document.referrer.includes('/user/login') ||
                       !sessionStorage.getItem('hasLoggedInThisSession');

    if (isFromLogin) {
      // 如果是登录后首次进入，清除提醒标记
      sessionStorage.removeItem('wechatBindingReminderShownThisSession');
    }

    // 添加短暂延迟，确保页面完全加载后再检查
    const timer = setTimeout(() => {
      checkBindingStatus();
    }, 1500); // 比待处理内容检查稍晚一点，避免同时弹出两个弹窗

    return () => clearTimeout(timer);
  }, [triggerReminder]);

  // 绑定成功回调
  const handleBindingSuccess = () => {
    setShowBindingModal(false);
    setShowReminder(false);
    checkBindingStatus(); // 重新检查状态
    onBindingSuccess?.();
  };

  // 关闭提醒
  const handleCloseReminder = () => {
    setShowReminder(false);
    // 标记已显示过提醒，避免重复显示
    sessionStorage.setItem('wechatBindingReminderShownThisSession', 'true');
  };

  // 打开绑定弹窗
  const handleOpenBinding = () => {
    setShowBindingModal(true);
    setShowReminder(false);
    // 标记已显示过提醒，避免重复显示
    sessionStorage.setItem('wechatBindingReminderShownThisSession', 'true');
  };

  // 稍后提醒
  const handleRemindLater = () => {
    setShowReminder(false);
    // 标记已显示过提醒，避免重复显示
    sessionStorage.setItem('wechatBindingReminderShownThisSession', 'true');
    // 30分钟后再次提醒
    setTimeout(() => {
      checkBindingStatus();
    }, 30 * 60 * 1000);
  };

  return (
    <>
      {/* 绑定提醒弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrcodeOutlined style={{ color: '#1890ff' }} />
            <span>微信绑定提醒</span>
          </div>
        }
        open={showReminder}
        onCancel={handleCloseReminder}
        footer={null}
        width={400}
        centered
        closable={false}
        maskClosable={false}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <QrcodeOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4}>绑定微信账号</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            绑定微信后，您可以及时收到系统通知和重要消息提醒
          </Text>

          <Space size="middle">
            <Button onClick={handleRemindLater}>
              稍后提醒
            </Button>
            <Button type="primary" onClick={handleOpenBinding}>
              立即绑定
            </Button>
          </Space>
        </div>
      </Modal>

      {/* 绑定弹窗 */}
      <WechatBindingModal
        visible={showBindingModal}
        onClose={() => setShowBindingModal(false)}
        onSuccess={handleBindingSuccess}
      />
    </>
  );
};

export default WechatBindingChecker;
